<template>
  <div class="w-full h-full flex flex-row items-center gap-3">
    <div class="w-1/4 h-full p-5 logistic-container">
      <span class="self-start font-semibold">
        All Sites
      </span>
      <bar-chart
        class="w-full h-full"
        :key="randomKey.barChart"
        :data="summaryChartData"
        :options="floatingBarChartOptions"
        :plugins="floatingBarChartPlugins"
      />
    </div>
    <div class="w-3/4 h-full p-5 logistic-container items-center overflow-hidden">
      <div class="overflow-x-auto w-full 2xl:flex 2xl:justify-center">
        <table
          v-if="tableData"
          :key="randomKey.table"
          class="w-full h-full border-collapse"
        >
          <thead>
            <tr>
              <th>
                Company
              </th>
              <th
                v-for="(header) of tableHeaders"
                :key="header"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(company) of companies"
              :key="company"
              class="h-full"
            >
              <td class="text-center">
                {{ company }}
              </td>
              <td
                v-for="(activity) of tableHeaders"
                :key="`${company}-${activity}`"
                class="h-full"
              >
                <div
                  v-if="isCompanyHasActivity(company, activity)"
                  class="p-5 flex flex-row items-center justify-center gap-4"
                  :class="[
                    getCellStyleAttribute(company, activity)
                  ].join(' ')"
                >
                  <div class="flex flex-col items-center justify-center gap-2">
                    <span class="actual font-semibold">
                      {{ getCellDetail(company, activity, 'actual') }}
                    </span>
                    <span>
                      {{ getCellDetail(company, activity, 'budget') }}
                    </span>
                  </div>
                  <div class="p-1 pb-3 flex flex-col items-center justify-start bg-white rounded-md">
                    <vs-icon
                      :icon="getCellStyleAttribute(company, activity, 'icon')
                      "
                      :color="getCellStyleAttribute(company, activity)"
                      size="25px"
                    />
                    <span class="percentage text-sm font-semibold">
                      {{ getCellDetail(company, activity, 'percentage') }}%
                    </span>
                  </div>
                </div>
                <div
                  v-else
                  class="h-full flex items-center justify-center text-center font-semibold gray"
                >
                  No Data
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex flex-row items-center justify-center gap-5">
        <div class="flex flex-row items-center gap-1">
          <div class="capsule red" />
          <span class="text-sm"> > Budget </span>
        </div>
        <div class="flex flex-row items-center gap-1">
          <div class="capsule green" />
          <span class="text-sm"> &lt;  Budget </span>
        </div>
        <div class="flex flex-row items-center gap-1">
          <div class="capsule light-gray" />
          <span class="text-sm"> = Budget </span>
        </div>
        <div class="flex flex-row items-center gap-1">
          <div class="capsule gray" />
          <span class="text-sm"> No Data </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {sortBy, uniqBy} from "lodash";

import BarChart from "@/components/barChart/index.vue";

export default {
  name: 'LogisticCostPerActivityCard',
  props: {
    summaryData: {
      type: Array,
      default: () => []
    },
    activityData: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'ptd'
    }
  },
  components: {
    BarChart
  },
  data() {
    return {
      summaryChartData: null,
      tableHeaders: [],
      tableData: null,
      companies: [],
      randomKey: {
        barChart: Date.now(),
        table: Date.now()
      }
    }
  },
  watch: {
    summaryData: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val) {
          this.summaryChartData = this.processSummaryData(val)
          this.randomKey.barChart = Date.now()
        } else {
          this.summaryChartData = null
          this.randomKey.barChart = Date.now()
        }
      }
    },
    activityData: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val) {
          this.tableData = val

          this.companies = this.tableData.map(({ company }) => company)

          const activities = this.tableData.reduce((arr, { activities }) => {
            arr.push(...activities.map(({activity_name, order}) => ({
              activity_name,
              order
            })))
            return arr
          }, [])
          this.tableHeaders = uniqBy(activities, 'activity_name')
            .sort((a, b) => a.order - b.order)
            .map(({ activity_name }) => activity_name)
          this.randomKey.table = Date.now()
        } else {
          this.tableData = null
          this.randomKey.table = Date.now()
        }
      }
    }
  },
  computed: {
    activitySequenceOrder() {
      const activityMap = new Map()
      activityMap.set('time charter', 1)
      activityMap.set('long term charter', 2)
      activityMap.set('fuel for barge', 3)
      activityMap.set('agency fee', 4)
      activityMap.set('freight charter', 5)
      activityMap.set('fts rental', 6)
      activityMap.set('stevedore', 7)

      return activityMap
    },
    floatingBarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            offset: true,
            grid: {
              display: false,
              borderColor: '#000',
              borderWidth: 2,
              drawBorder: true
            },
            ticks: {
              display: false
            }
          },
          y: {
            offset: true,
            grid: {
              display: false,
              borderColor: '#000',
              borderWidth: 2,
              drawBorder: true
            },
            ticks: {
              display: true,
              font: { size: 12, family: 'Poppins' },
              color: 'rgba(0, 0, 0, 1)'
            }
          }
        },
        layout: {
          padding: {
            top: 30,
            right: 10,
            left: 0,
            bottom: 80
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            backgroundColor: '#FAFDFF',
            titleColor: '#000000',
            titleFont: {
              weight: 'Bold',
              family: 'Poppins'
            },
            titleAlign: 'center',
            bodyColor: '#000000',
            bodyFont: {
              family: 'Poppins'
            },
            bodyAlign: 'center',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              title(labelItem) {
                return labelItem?.[0]?.label
              },
              label(tooltipItem) {
                return `${(tooltipItem?.raw?.[1] - tooltipItem?.raw?.[0])?.toFixed(2)} USD/Ton`
              }
            }
          },
          customTitle: {
            y: {
              display: true,
              text: 'USD/Ton',
              color: '#000000',
              font: {
                family: 'Poppins',
                size: 11
              }
            }
          }
        }
      }
    },
    floatingBarChartPlugins() {
      return [
        this.htmlLegendPlugin(),
        this.axisIconPlugin(),
        this.customTitlePlugin()
      ]
    }
  },
  methods: {
    processSummaryData(data) {
      const ACTIVITY_CONFIG = [
        {
          activity_name: 'Time Charter',
          color: '#32AAE6'
        },
        {
          activity_name: 'Freight Charter',
          color: '#ff99ff'
        },
        {
          activity_name: 'FTS Rental',
          color: '#33B57D'
        },
        {
          activity_name: 'Stevedore',
          color: '#FFCF31'
        },
        {
          activity_name: 'Total',
          color: '#6546C3'
        }
      ]

      const processedActivities = data.map(activity => ({
        ...activity,
        color: ACTIVITY_CONFIG.find(act => act.activity_name === activity.activity_name) ? ACTIVITY_CONFIG.find(act => act.activity_name === activity.activity_name).color : '#6546C3' 
      }))

      const sortedActivities = sortBy(processedActivities, 'order')
      const labels = [
        ...sortedActivities.map(({ activity_name }) => activity_name)
      ]

      const dataset = sortedActivities.reduce((arr, activity, index) => {
        if(activity.activity_name !== 'Total') {
          const begin = arr.length === 0 ? 0 : arr[index - 1][1]
          const end = activity?.value + begin
          arr.push([begin, end])
        } else{
          arr.push([0, activity?.value])
        }

        return arr
      }, []);

      const datasets = [{
        label: "All Sites",
        data: [...dataset],
        barThickness: 30,
        borderRadius: 5,
        borderSkipped: false,
        backgroundColor: ACTIVITY_CONFIG.map((key) => key.color)
      }]
      return {
        labels: labels,
        datasets: datasets
      }
    },
    isCompanyHasActivity(company, activityName) {
      const isExists = this.tableData
        ?.find(({ company: companyCode }) => companyCode === company)
        ?.activities
        ?.find(({ activity_name }) => activity_name === activityName)

      if (isExists) {
        return !Object.keys(isExists)
          .filter((key) => key === 'actual' || key === 'budget' || key === 'percentage')
          .every((key) => isExists[key] === 0)
      }

      return false
    },
    getCellDetail(company, activityName, key) {
      const activityCost = this.tableData
        ?.find(({ company: companyCode }) => companyCode === company)
        ?.activities
        ?.find(({ activity_name }) => activity_name === activityName) || null
      if (activityCost === null) {
        return '-'
      }
      if (key === 'percentage') return parseInt(activityCost[key])
      return parseFloat(Number(activityCost[key]).toFixed(2)) || (activityCost[key] === null && key ==='percentage' ? 0 : '-')
    },
    getCellStyleAttribute(company, activityName, type = 'class') {
      const activityCost = this.tableData
        ?.find(({ company: companyCode }) => companyCode === company)
        ?.activities
        ?.find(({ activity_name }) => activity_name === activityName) || null
      if (activityCost === null) {
        return ''
      }
      const actual = activityCost?.['actual']
      const budget = activityCost?.['budget']
      const bad = (actual === 0 && budget !== 0) || (actual !== 0 && budget !== 0 && actual > budget)
      const good = (actual !== 0 && budget === 0) || (actual !== 0 && budget !== 0 && actual < budget)
      const even = (actual < budget) && (actual !== 0 && budget !== 0)

      if (bad) {
        return type === 'class' ? 'red' : 'arrow_drop_up'
      } else if (good) {
        return type === 'class' ? 'green' : 'arrow_drop_down'
      } else if (even) {
        return type === 'class' ? 'light-gray' : 'check_indeterminate_small'
      }

      return even
    },
    htmlLegendPlugin() {
      return {
        id: 'custom-legend',
        afterDraw(chart) {
          const ctx = chart.ctx;

          const chartArea = chart.chartArea;

          const chartData = chart.data
          const legendItems = chartData.labels

          // Calculate position at bottom center
          const legendX = chartArea.left;
          const legendY = chartArea.bottom + 20;
          const itemWidth = 120; // Width of each legend item
          const itemHeight = 25;
          const itemsPerRow = Math.ceil(legendItems.length / 3);

          ctx.save();
          ctx.font = '11px Poppins';

          // Draw legend items in two rows
          legendItems
            .forEach((item, i) => {
              const row = Math.floor(i / itemsPerRow);
              const col = (i + 1) % itemsPerRow;
              const padding = col === 1 ? 0 : -10

              const x = legendX + chartArea.width / 2  - col * itemWidth + padding;
              const y = legendY + row * itemHeight;

              // Draw colored box
              ctx.beginPath();
              ctx.roundRect(x, y, 25, 8, 5);
              ctx.fillStyle = chartData?.datasets[0]?.backgroundColor[i];
              ctx.fill();

              // Draw text
              ctx.fillStyle = '#666';
              ctx.fillText(item.charAt(0).toUpperCase() + item.slice(1), x + 30, y + 8);
            });

          ctx.restore();
        }
      }
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;

          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };

          const rightIcon = {
            text: '\u2BC8',
            x: right,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }

          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();

          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    },
    customTitlePlugin() {
      return {
        id: 'customTitle',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, width } } = chart
          const axisYTicksWidth = chart.scales.y.width + 10

          if (options?.y?.display) {
            ctx.save()
            ctx.fillStyle = options.y.color || 'black'

            const font = options.y.font || {}
            const fontSize = font.size || 10;
            const fontFamily = font.family || 'sans-serif'
            const fontWeight = font.weight || 'normal'
            ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'

            const xPos = left - axisYTicksWidth / 4 + ctx.measureText(options.y.text).width / 4
            const yPos = top - 25

            ctx.fillText(options.y.text, xPos, yPos)
            ctx.restore()
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
table {
  thead {
    th {
      @apply p-2 text-center text-base;
      border-bottom: 2px solid #D7D2E4;
    }
  }
  tbody {
    td {
      border-bottom: 2px solid #D7D2E4;
      border-right: 2px solid white;
    }

    tr:last-child {
      td {
        border-bottom: 1px solid #D7D2E4;
      }
    }
  }
}

.logistic-container {
  @apply p-3 flex flex-col items-center gap-5 bg-white rounded-lg shadow-md;
}

.gray {
  @apply text-white;
  background: #606060;
}

.green {
  background: #ADFFDC;

  .actual, .percentage {
    color: #33B57D;
  }
}

.red {
  background: #FFCED9;

  .actual, .percentage {
    color: #FF3662;
  }
}

.capsule {
  width: 34px;
  height: 10px;
  border-radius: 5px;

  &.green {
    background: #ADFFDC;
  }

  &.red {
    background: #FFCED9;
  }

  &.light-gray {
    background: #D7D2E4;
  }

  &.grey {
    background: #606060;
  }
}
</style>